import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { Loader, Banner, Breadcrumbs, Table, LayerCard, Empty, Badge } from '@cloudflare/kumo'
import { Users, House } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { Coworker } from '@hub-wifi/shared'

export function CoworkersPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const [coworkers, setCoworkers] = useState<Coworker[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadCoworkers() {
      try {
        const token = await getAccessTokenSilently()
        const data = await apiRequest<Coworker[]>('/api/coworkers', { token })
        setCoworkers(data)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
      } finally {
        setLoading(false)
      }
    }
    void loadCoworkers()
  }, [getAccessTokenSilently])

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<House size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('coworkers.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('coworkers.title')}
        description={t('coworkers.description')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && coworkers.length === 0 && (
        <Empty
          icon={<Users size={48} className="text-kumo-inactive" />}
          title={t('coworkers.empty')}
        />
      )}

      {!loading && !error && coworkers.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>{t('coworkers.name')}</Table.Head>
                  <Table.Head>{t('coworkers.email')}</Table.Head>
                  <Table.Head>{t('coworkers.status')}</Table.Head>
                  <Table.Head>{t('coworkers.company')}</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {coworkers.map((coworker) => (
                  <Table.Row key={coworker.id}>
                    <Table.Cell>{coworker.name}</Table.Cell>
                    <Table.Cell>{coworker.email}</Table.Cell>
                    <Table.Cell>
                      <Badge variant={coworker.status === 'active' ? 'primary' : 'secondary'}>
                        {coworker.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>{coworker.companyId ?? t('common.b2c')}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
